import { ToggleGroup } from "radix-ui";

import { cn } from "./cn";

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

/**
 * One choice from a few (e.g. a status filter). Arrow keys move between options; on narrow screens the row
 * scrolls sideways instead of wrapping.
 */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  fill = false,
  className,
}: {
  /** Read by screen readers ("Status"). */
  label: string;
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Stretch across the row, options sharing the width (a few short options in a narrow card). */
  fill?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "-mx-1 [scrollbar-width:none] overflow-x-auto px-1 [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      <ToggleGroup.Root
        type="single"
        aria-label={label}
        value={value}
        // Radix sends "" when the active option is clicked again; a filter always keeps one choice.
        onValueChange={(next) => next && onChange(next as T)}
        className={cn(
          "h-11 items-center gap-0.5 rounded-xl border border-border bg-surface p-[3px]",
          fill ? "flex w-full" : "inline-flex",
        )}
      >
        {options.map((option) => (
          <ToggleGroup.Item
            key={option.value}
            value={option.value}
            className={cn(
              "h-9 rounded-[9px] text-sm font-semibold whitespace-nowrap transition-colors",
              fill ? "flex-1 px-2" : "px-3.5",
              "text-muted hover:text-ink data-[state=on]:bg-primary-soft data-[state=on]:text-primary-soft-ink",
            )}
          >
            {option.label}
          </ToggleGroup.Item>
        ))}
      </ToggleGroup.Root>
    </div>
  );
}
